import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article";
  publishedAt?: string;
  author?: string;
  jsonLd?: Record<string, any>;
}

const SITE = "https://loveotherwise.com";
const DEFAULT_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/d6aabce5-8e2b-43e0-9e3a-d5a418b1700f/id-preview-605bd2c6--291df475-de41-4e94-a372-2043aaffbd63.lovable.app-1776348400732.png";

const setMeta = (selector: string, attr: string, attrValue: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  publishedAt,
  author,
  jsonLd,
}: SEOProps) => {
  useEffect(() => {
    const url = canonical
      ? canonical.startsWith("http")
        ? canonical
        : `${SITE}${canonical}`
      : SITE;
    const safeTitle = title.length > 60 ? title.slice(0, 57) + "…" : title;
    const safeDesc =
      description.length > 160 ? description.slice(0, 157) + "…" : description;

    document.title = safeTitle;
    setMeta('meta[name="description"]', "name", "description", safeDesc);
    setLink("canonical", url);

    setMeta('meta[property="og:type"]', "property", "og:type", type);
    setMeta('meta[property="og:title"]', "property", "og:title", safeTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", safeDesc);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:image"]', "property", "og:image", image);

    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", safeTitle);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", safeDesc);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    if (publishedAt) {
      setMeta(
        'meta[property="article:published_time"]',
        "property",
        "article:published_time",
        publishedAt
      );
    }
    if (author) {
      setMeta('meta[property="article:author"]', "property", "article:author", author);
    }

    let ldEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.setAttribute("data-seo-jsonld", "page");
      ldEl.text = JSON.stringify(jsonLd);
      // Remove any prior page-level JSON-LD before adding new
      document
        .querySelectorAll('script[data-seo-jsonld="page"]')
        .forEach((n) => n.remove());
      document.head.appendChild(ldEl);
    }

    return () => {
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
    };
  }, [title, description, canonical, image, type, publishedAt, author, jsonLd]);

  return null;
};

export default SEO;
